import {
  type Date__Input,
  type Lfa_Conversion_Data_Rejection_Criteria,
} from '~/graphql/types';

export type State = {
  reasonType: 'rescheduled' | 'rejected' | null;
  rejectionReasons: Array<Lfa_Conversion_Data_Rejection_Criteria>;
  rescheduledDate: Date__Input | null;
  optionalNote: string;
  customReasonText: string;
};

export type ReducerAction =
  | {
      type: 'SET_REASON_TYPE';
      payload: { reasonType: 'rescheduled' | 'rejected' | null };
    }
  | {
      type: 'SET_REJECTION_REASONS';
      payload: {
        rejectionReasons: Array<Lfa_Conversion_Data_Rejection_Criteria>;
      };
    }
  | {
      type: 'TOGGLE_REJECTION_REASON';
      payload: { reason: Lfa_Conversion_Data_Rejection_Criteria };
    }
  | { type: 'SET_RESCHEDULED_DATE'; payload: { date: Date__Input | null } }
  | { type: 'SET_OPTIONAL_NOTE'; payload: { note: string } }
  | { type: 'SET_CUSTOM_REASON_TEXT'; payload: { text: string } }
  | { type: 'RESET_STATE' };

export const initialState: State = {
  reasonType: 'rejected',
  rejectionReasons: [],
  rescheduledDate: null,
  optionalNote: '',
  customReasonText: '',
};

const reducer = (state: State, action: ReducerAction): State => {
  switch (action.type) {
    case 'SET_REASON_TYPE':
      return {
        ...state,
        reasonType: action.payload.reasonType,
        // Clear related fields when switching reason types
        ...(action.payload.reasonType === 'rescheduled' && {
          rejectionReasons: [],
          customReasonText: '',
        }),
        ...(action.payload.reasonType === 'rejected' && {
          rescheduledDate: null,
          optionalNote: '',
        }),
      };

    case 'SET_REJECTION_REASONS':
      return {
        ...state,
        rejectionReasons: action.payload.rejectionReasons,
      };

    case 'TOGGLE_REJECTION_REASON': {
      const { reason } = action.payload;
      const exists = state.rejectionReasons.some(r => r.id === reason.id);

      const updatedReasons = exists
        ? state.rejectionReasons.filter(r => r.id !== reason.id)
        : [...state.rejectionReasons, reason];

      // Clear custom reason text if no custom reason is selected
      const hasCustomReason = updatedReasons.some(
        r => r.id === 'custom_reason',
      );

      return {
        ...state,
        rejectionReasons: updatedReasons,
        customReasonText: hasCustomReason ? state.customReasonText : '',
      };
    }

    case 'SET_RESCHEDULED_DATE':
      return {
        ...state,
        rescheduledDate: action.payload.date,
      };

    case 'SET_OPTIONAL_NOTE':
      return {
        ...state,
        optionalNote: action.payload.note,
      };

    case 'SET_CUSTOM_REASON_TEXT':
      return {
        ...state,
        customReasonText: action.payload.text,
      };

    case 'RESET_STATE':
      return initialState;

    default:
      return state;
  }
};

export default reducer;
